// public/js/app.js
import { checkAuth, showUser, showLogin, clearAuth, initiateLogout,showLoggedOutScreen } from './modules/auth.js';
import { initRouter } from './router.js';
import { initMapPage } from './pages/map.js';
import { initAssetsPage } from './pages/assets.js';
import { initDevicesPage } from './pages/devices.js';
import { initTrackerConfigPage } from './features/tracker-config/tracker-config-page.js';
import { featureEnabled } from './modules/features.js';
import { initUsersPage } from './pages/users.js';
import { initTenantsPage } from './pages/tenants.js';
import { can, canExport, setEntitlements } from './modules/rbac.js';
import { initSettingsPage } from './pages/settings.js';
import { initReportsPage } from './pages/reports.js';
import { authenticatedFetch } from './modules/auth.js';
import { initDemoMode, refreshDemoSwitch } from './modules/demo-mode.js';
import { initPoiNav } from './features/pois/poi-nav.js';
import { initAlertsPage } from './features/alerts/alerts-page.js';
import { startAlertNotifications } from './features/alerts/alert-notifications.js';
import { initFeedbackPage, refreshFeedbackBadge } from './features/feedback/feedback-page.js';
import { initSubscriptionPage } from './features/subscription/subscription-page.js';
import { initDistributionPage } from './features/distribution/distribution-page.js';
import { initDeliveriesPage } from './features/distribution/deliveries-page.js';
import { initAnnouncementsPage } from './features/announcements/announcements-page.js';
import { checkWhatsNew } from './features/announcements/boot.js';
import { initUserAnalyticsPage } from './features/user-analytics/user-analytics-page.js';
import { initManualPage, openManualModal } from './features/manual/manual-page.js';
import { initFilesPage } from './features/files/explorer-page.js';
import { initAnalytics } from './modules/analytics.js';

import './modules/ui/spinner.js';

import { createAppLayout } from './ui/layout.js';
import { createAllModals } from './ui/modals.js';
import { initBreadcrumb } from './ui/breadcrumb.js';
import { initTenantScope, SCOPE_CHANGE_EVENT, scopeTenantParam } from './modules/tenant-scope.js';
import { loadTemplates, invalidateTemplates } from './features/display/template-store.js';
import { initTenantScopeWidget } from './ui/tenant-scope-widget.js';

async function main() {
  const user = await checkAuth();
  if (!user) {
    showLoggedOutScreen();
    return;
  }
  // Resolve add-on entitlements (data export) up front so the nav can gate the
  // Reports link, and keep the settings payload — the tenant-scope mirror lives
  // in it. Best-effort: a failure just leaves export features hidden.
  let bootSettings = {};
  try {
    const res = await authenticatedFetch('api/v1/settings');
    if (res.ok) {
      const data = await res.json();
      setEntitlements(data.entitlements || {});
      bootSettings = data.settings || {};
    }
  } catch { setEntitlements({}); }

  // Global tenant scope (docs/tenant-scope.md): resolve memberships + the
  // initial scope BEFORE the first page loads, so every list starts scoped.
  try { await initTenantScope(bootSettings); } catch (e) { console.warn('[app] tenant scope init failed', e); }

  // Display templates (docs/asset-display-templates.md): resolve the tenant
  // default merged with this user's override before the first render, so list
  // columns / map card / pin labels paint correctly on the very first paint.
  // Best-effort — every consumer falls back to the built-in defaults.
  try { await loadTemplates(scopeTenantParam()); } catch (e) { console.warn('[app] display templates init failed', e); }

  createAppLayout();
  createAllModals();
  showUser(user);
  initNavigation(user);
  initBreadcrumb();
  initTopbarControls();
  initTenantScopeWidget();

  // A scope change re-renders the ACTIVE page: re-fire the router on the
  // current hash — every page re-inits and loads its (now scoped) data.
  window.addEventListener(SCOPE_CHANGE_EVENT, async (e) => {
    // Templates are per tenant — drop the resolved set and reload it for the
    // new scope BEFORE the page re-renders, or the first paint uses stale
    // columns/labels.
    try { invalidateTemplates(); await loadTemplates(scopeTenantParam()); } catch (e) {}

    // An asset DETAIL page cannot survive a scope the user picked. Re-rendering
    // it calls ensureScopeFor(asset.tenantId), which pulls the scope straight
    // back to the asset's OWN tenant - so choosing a tenant from a detail page
    // silently landed you on the previous one instead ("I select Viva and it
    // brings me to Air Canada", reported 2026-10-20). Leave for the list, which
    // is what picking a tenant means here.
    //
    // Only for a USER choice: reason 'auto' is ensureScopeFor itself following
    // a deep link (alert mail, shared URL), and that must keep the page open.
    const hash = window.location.hash || '';
    if (e?.detail?.reason !== 'auto' && /^#assets\/[^/]+/.test(hash)) {
      window.location.hash = '#assets';   // fires hashchange on its own
      return;
    }
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  });

  // Device-manual links (device list, paired-device cards) open a pop-up modal
  // with the product's manual content, instead of navigating away. The full
  // #manual page stays reachable from the topbar/sidebar and the modal footer.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a.manual-link[href^="#manual/"]');
    if (!link) return;
    e.preventDefault();
    // The device slug is the LAST path segment (#manual/devices/<slug> or the
    // legacy #manual/<slug>), so the pop-up modal opens the right product.
    openManualModal(decodeURIComponent(link.getAttribute('href').split('/').pop() || ''));
  });

  // Routes for a feature this environment cannot serve are not registered at
  // all, so a bookmarked or hand-typed address falls back to the map instead of
  // opening a page whose every request would 404.
  const routes = {
    '#map': initMapPage,
    '#assets': initAssetsPage,
    '#devices': initDevicesPage,
    '#users': initUsersPage,
    '#tenants': initTenantsPage,
    '#settings': initSettingsPage,
    '#alerts': initAlertsPage,
    '#feedback': initFeedbackPage,
    '#reports': initReportsPage,
    '#subscription': initSubscriptionPage,
    '#distribution': initDistributionPage,
    '#deliveries': initDeliveriesPage,
    '#whats-new': initAnnouncementsPage,
    '#user-activity': initUserAnalyticsPage,
    '#manual': initManualPage,
  };
  if (featureEnabled('trackerConfig')) routes['#tracker-config'] = initTrackerConfigPage;
  if (featureEnabled('assetFiles'))    routes['#files'] = initFilesPage;
  initRouter(routes);

  if (!window.location.hash) window.location.hash = '#map';
  window.CURRENT_USER = user;

  // Sidebar "Demo" switch: one click into the demo tenant and its map. Reads the
  // user's memberships, so it must run after the tenant scope is ready, and it
  // re-reads them on every scope change to stay in step (docs/demo-tenant.md).
  initDemoMode();
  window.addEventListener(SCOPE_CHANGE_EVENT, () => refreshDemoSwitch());

  // App-wide alert notifications (browser toast + desktop Notification + badge).
  startAlertNotifications();
  // Feedback unread badge (tickets in my/tenant boards with new activity).
  refreshFeedbackBadge();
  // "What's new": show the modal for any unread announcements + set the badge.
  checkWhatsNew();
  // Start the lightweight activity collector (feeds the superadmin analytics view).
  initAnalytics();
}

// ── Collapsible sidebar groups ──────────────────────────────────────────────
// Open state is remembered per browser, but a group is ALWAYS open while you
// are on one of its pages — otherwise the active item would be hidden inside a
// closed group and you could not see where you are.
const NAV_GROUPS_KEY = 'nav.openGroups';
const readOpenGroups = () => {
  try { return new Set(JSON.parse(localStorage.getItem(NAV_GROUPS_KEY) || '[]')); }
  catch { return new Set(); }
};
const writeOpenGroups = (set) => {
  try { localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify([...set])); } catch { /* private mode */ }
};

/** Which group holds the page we are on, if any. */
function groupOfCurrentPage() {
  const base = (window.location.hash || '#map').split('/')[0];
  const link = document.querySelector(`#nav-menu .nav-group a[href="${base}"]`);
  return link?.closest('.nav-group')?.dataset.group || null;
}

function applyNavGroupState() {
  const open = readOpenGroups();
  const active = groupOfCurrentPage();
  document.querySelectorAll('#nav-menu .nav-group').forEach(g => {
    const id = g.dataset.group;
    const isOpen = open.has(id) || id === active;
    g.classList.toggle('open', isOpen);
    g.querySelector('.nav-group-head')?.setAttribute('aria-expanded', String(isOpen));
  });
}

function initNavGroups() {
  document.querySelectorAll('#nav-menu .nav-group-head').forEach(head => {
    const toggle = () => {
      const g = head.closest('.nav-group');
      const id = g.dataset.group;
      const open = readOpenGroups();
      // Closing a group you are currently inside would hide the active page, so
      // applyNavGroupState re-opens it — remember the intent anyway for later.
      if (open.has(id)) open.delete(id); else open.add(id);
      writeOpenGroups(open);
      applyNavGroupState();
    };
    head.addEventListener('click', toggle);
    head.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); toggle(); }
    });
  });
  applyNavGroupState();
  window.addEventListener('hashchange', applyNavGroupState);
}

function initNavigation(user) {
  const groups = user?.groups || [];

  // === Dynamic menu generation based on real permissions (corrected) ===
  // Each link is shown ONLY if the user has the matching permission.
  // Map → viewMap (or listAssets as fallback)
  // Assets → listAssets
  // Devices → listDevices (strict: TenantAdmin+ only)
  // Users → listUsers
  // Tenants → listTenants (corrected, was wrongly listAssets before)
  // Settings → listSettings (almost all roles)
  // The top level is deliberately short — Map, Assets and two groups. Everything
  // else hangs under a group, indented, so the sidebar reads as a hierarchy
  // instead of a list of twelve equal things.
  const link = (href, icon, label, extra = '', cls = '') =>
    `<a href="${href}" class="nav-link ${cls}"><i class="fa-solid ${icon} me-3"></i><span>${label}</span>${extra}</a>`;

  // A collapsible group. It is NOT a link: clicking the head only opens and
  // closes it, so nobody navigates by accident (and it has no page of its own).
  const group = (id, icon, label, children) => children.length ? `
    <div class="nav-group" data-group="${id}">
      <div class="nav-link nav-group-head" role="button" tabindex="0" aria-expanded="false" aria-controls="nav-group-${id}">
        <i class="fa-solid ${icon} me-3"></i><span>${label}</span>
        <i class="fa-solid fa-chevron-down nav-group-chevron ms-auto"></i>
      </div>
      <div class="nav-group-body" id="nav-group-${id}"><div class="nav-group-inner">${children.join('')}</div></div>
    </div>` : '';

  let menuHTML = '';

  if (can(groups, 'viewMap') || can(groups, 'listAssets')) {
    menuHTML += link('#map', 'fa-map', 'Map');
    // POIs are inserted right here by features/pois/poi-nav.js as a sub-item.
  }

  if (can(groups, 'listAssets')) {
    menuHTML += link('#assets', 'fa-boxes-stacked', 'Assets');
  }

  // ── Operations: the day-to-day work that is not the map or the asset list ──
  const ops = [];
  // Reports (data-export add-on) — only when the tenant holds the entitlement.
  if (canExport()) ops.push(link('#reports', 'fa-file-arrow-down', 'Reports', '', 'nav-sub'));
  // Alerts are personal (per user) — shown to anyone who can see assets.
  if (can(groups, 'viewMap') || can(groups, 'listAssets')) {
    ops.push(link('#alerts', 'fa-bell', 'Alerts', '<span id="alerts-nav-badge" class="badge bg-danger ms-auto d-none">0</span>', 'nav-sub'));
  }
  // Files explorer — any tenant member may browse their documents (uploading
  // needs manageAssetFiles, which the page itself gates). Feature-flagged.
  if (featureEnabled('assetFiles') && can(groups, 'listAssets')) {
    ops.push(link('#files', 'fa-folder-open', 'Files', '', 'nav-sub'));
  }
  if (can(groups, 'listDevices')) ops.push(link('#devices', 'fa-satellite-dish', 'Devices', '', 'nav-sub'));
  // Deliveries: a tenant admin's own device orders with tracking (read-only).
  if (can(groups, 'viewDeliveries')) ops.push(link('#deliveries', 'fa-truck-fast', 'Deliveries', '', 'nav-sub'));
  menuHTML += group('operations', 'fa-diagram-project', 'Operations', ops);

  // ── Administration: who may do what, and how the hardware is configured ──
  const admin = [];
  if (can(groups, 'listUsers')) admin.push(link('#users', 'fa-users', 'Users', '', 'nav-sub'));
  if (can(groups, 'listTenants')) admin.push(link('#tenants', 'fa-building', 'Tenants', '', 'nav-sub'));
  // Gated by a feature flag AND a role: the flag says whether this environment
  // can serve it at all, the role who may use it (modules/features.js).
  if (featureEnabled('trackerConfig') && can(groups, 'manageDeviceConfig')) {
    admin.push(link('#tracker-config', 'fa-sliders', 'Tracker config', '', 'nav-sub'));
  }
  menuHTML += group('administration', 'fa-shield-halved', 'Administration', admin);

  // (Device manual is reached via the book icon in the topbar, not the sidebar.)

  const nav = document.getElementById('nav-menu');
  if (nav) nav.innerHTML = menuHTML;

  // Footer: Settings, then Feedback, bottom-aligned. Logout used to sit below
  // them and is gone — it was already in the user dropdown.
  const navFooter = document.getElementById('nav-footer');
  if (navFooter) {
    let footHTML = '';
    if (can(groups, 'listSettings')) footHTML += link('#settings', 'fa-gear', 'Settings');
    footHTML += link('#feedback', 'fa-comments', 'Feedback',
      '<span id="feedback-nav-badge" class="badge bg-danger ms-auto d-none">0</span>');
    navFooter.innerHTML = footHTML;
  }

  initNavGroups();

  // ============================================
  // Manage the active menu item (unchanged)
  // ============================================
  // ANCHORS only. A group head also carries .nav-link for styling but has no
  // href — including it let a click on "Operations" steal the active highlight
  // from the page you were actually on, and nothing gave it back until the next
  // navigation.
  const navLinks = document.querySelectorAll('#nav-menu a.nav-link, #nav-footer a.nav-link');

  function setActiveNavLink() {
    // Compare BASE paths, not the whole hash: a sub-path such as
    // #settings/doctypes, #assets/<uuid> or #distribution/inventory must keep
    // its sidebar entry highlighted (an exact match dropped it entirely).
    const base = (window.location.hash || '#map').split('/')[0];
    navLinks.forEach(link => {
      link.classList.toggle('active', (link.getAttribute('href') || '').split('/')[0] === base);
    });
  }

  // Set the initial active item
  setActiveNavLink();

  // Update on hash change (router)
  window.addEventListener('hashchange', setActiveNavLink);

  // Immediate feedback on click
  navLinks.forEach(link => {
    link.addEventListener('click', () => {
      navLinks.forEach(l => l.classList.remove('active'));
      link.classList.add('active');
    });
  });

  // Mobile Sidebar Toggle
  const mobileToggle = document.getElementById('mobile-sidebar-toggle');
  const sidebar = document.getElementById('sidebar');

  if (mobileToggle && sidebar) {
    mobileToggle.addEventListener('click', () => {
      sidebar.classList.toggle('mobile-open');
    });

    // One way to close the drawer, so a flow that TAKES OVER the map (the POI
    // place mode) can get the sidebar out of its own way. On a phone the drawer
    // covers most of the map, and starting place mode from the "+" inside it
    // used to leave almost nothing to aim at.
    window.closeMobileSidebar = () => {
      if (window.innerWidth < 768) sidebar.classList.remove('mobile-open');
    };

    navLinks.forEach(link => {
      link.addEventListener('click', () => window.closeMobileSidebar());
    });

    document.addEventListener('click', (e) => {
      if (window.innerWidth < 768 &&
          sidebar.classList.contains('mobile-open') &&
          !sidebar.contains(e.target) &&
          !mobileToggle.contains(e.target)) {
        sidebar.classList.remove('mobile-open');
      }
    });
  }

  // ============================================

  // POIs submenu (collapsible, default closed) — inserted under the Map link.
  initPoiNav(groups);
}

// ============================================
// Topbar-Controls: Glocke → Alerts, Metric/Imperial-Toggle, Sidebar-Collapse
// ============================================
function initTopbarControls() {
  // Notification bell → Alerts page
  document.getElementById('btn-notifications')?.addEventListener('click', () => {
    window.location.hash = '#alerts';
  });

  // The Metric/Imperial toggle was removed from the topbar — it is a set-once
  // preference and now lives only in Settings (docs/units.md). The rest of the
  // app still reacts to `unitsystemchange`, so nothing else had to move.

  // Sidebar collapse (desktop): the sidebar slides fully off-screen and a small
  // handle on the left edge brings it back. The map re-fits after the transition.
  const collapseBtn = document.getElementById('sidebar-collapse-toggle');
  const sidebar = document.getElementById('sidebar');
  const reopen = document.getElementById('sidebar-reopen');
  const afterSidebarResize = () => setTimeout(() => window.dispatchEvent(new Event('resize')), 320);
  const setCollapsed = (collapsed) => {
    sidebar.classList.toggle('collapsed', collapsed);
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    const icon = collapseBtn?.querySelector('i');
    if (icon) icon.className = collapsed ? 'fa-solid fa-angle-right' : 'fa-solid fa-angle-left';
    afterSidebarResize();
  };
  if (collapseBtn && sidebar) {
    collapseBtn.addEventListener('click', () => setCollapsed(!sidebar.classList.contains('collapsed')));
  }
  if (reopen && sidebar) {
    reopen.addEventListener('click', () => setCollapsed(false));
  }
}

// ============================================
// Global toast function (Bootstrap) - ORIGINAL
// ============================================
window.showToast = function (message, type = 'success', title = '') {
  const container = document.querySelector('.toast-container');
  if (!container) return console.warn('Toast container not found');

  const toastId = 'toast-' + Date.now();
  const icon = type === 'success' ? 'fa-check-circle' :
    type === 'danger' ? 'fa-exclamation-circle' :
      type === 'warning' ? 'fa-exclamation-triangle' : 'fa-info-circle';

  const toastHTML = `
    <div id="${toastId}" class="toast align-items-center text-white bg-${type} border-0 shadow" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="d-flex">
        <div class="toast-body d-flex align-items-center gap-2">
          <i class="fa-solid ${icon} fa-lg"></i>
          <div>
            ${title ? `<strong>${title}</strong><br>` : ''}
            ${message}
          </div>
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>
  `;

  container.insertAdjacentHTML('beforeend', toastHTML);
  const toastEl = document.getElementById(toastId);
  const toast = new bootstrap.Toast(toastEl, { delay: 4500, autohide: true });
  toast.show();

  // Remove the toast after it hides
  toastEl.addEventListener('hidden.bs.toast', () => toastEl.remove());
};

window.addEventListener('load', main);
